import Mustache from 'mustache';
import {
  FormSpecsSchema,
  FormSpecs,
  ProcessedFormSpecs,
  GenericObjectType,
  EmbedFormOptions,
  JSONSchema7,
  UiSchema,
} from '@/types';
import { mergeDeep } from './merge-deep';

const generateClassNameFromPropertyUiSchemaAndSchema = (
  uiSchema: any,
  schema: any
): string => {
  const type = schema.type || 'string';
  const uiClassName = uiSchema['ui:classNames'] || '';
  const widget = uiSchema['ui:widget'] || 'default';
  const field = uiSchema['ui:field'] || 'default';
  return `${uiClassName} yepcode-type-${type} yepcode-widget-${widget} yepcode-field-${field}`.trim();
};

const buildPropertyUiSchema = (
  [key, propertySchema]: [string, any],
  index: number,
  options: {
    focusOnFirst: boolean;
    isSensitive: boolean;
    globalUiSchema: UiSchema;
  }
): UiSchema => {
  const { ui, uiSchema, ['ui:schema']: uiColonSchema } = propertySchema;
  return {
    ...(index === 0 && options.focusOnFirst && { 'ui:autofocus': true }),
    ...(options.isSensitive && {
      'ui:widget': 'password',
    }),

    ...uiColonSchema,
    ...uiSchema,
    ...ui,
    ...(propertySchema.properties && {
      ...propertiesToUiSchema(
        propertySchema.properties,
        options.globalUiSchema
      ),
    }),
    ...(propertySchema.oneOf &&
      mergeDeep(
        {},
        ...propertySchema.oneOf.map((px: any) => {
          return propertiesToUiSchema(px.properties, options.globalUiSchema);
        })
      )),
    ...options.globalUiSchema[key],
  };
};

const propertiesToUiSchema = (
  properties: FormSpecsSchema['properties'],
  globalUiSchema: UiSchema,
  focusOnFirst: boolean = false
): UiSchema => {
  return Object.entries<UiSchema>(
    properties ?? ({} as GenericObjectType)
  ).reduce((acc: UiSchema, [key, value]: [string, any], index: number) => {
    const propertyUiSchema = buildPropertyUiSchema([key, value], index, {
      focusOnFirst,
      isSensitive: value.isSensitive,
      globalUiSchema,
    });
    return {
      ...acc,
      [key]: {
        ...propertyUiSchema,
        'ui:classNames': generateClassNameFromPropertyUiSchemaAndSchema(
          propertyUiSchema,
          value
        ),
      },
    };
  }, {});
};

const filterObjectKeys = (obj: any, keys: string[]) => {
  if (typeof obj != 'object') {
    return obj;
  }
  const result: any = {};
  for (const i in obj) {
    if (!obj.hasOwnProperty(i)) continue;
    if (!keys.includes(i)) {
      const value = obj[i];
      if (typeof value == 'object') {
        if (Array.isArray(value)) {
          result[i] = value.map((o) => filterObjectKeys(o, keys));
        } else {
          result[i] = filterObjectKeys(value, keys);
        }
      } else {
        result[i] = value;
      }
    }
  }
  return result;
};

const processSchema = (schema: FormSpecsSchema): JSONSchema7 => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { embedFormOptions, withBranding, ui, uiSchema, ...processedSchema } =
    schema;
  return filterObjectKeys(processedSchema, [
    'ui',
    'isSensitive',
    'isTransient',
  ]);
};

const processUiSchema = (formSpecs: FormSpecs): UiSchema => {
  const uiSchema = {
    ...formSpecs.schema.ui,
    ...formSpecs.schema.uiSchema,
  };
  let dependenciesUiSchema = {};
  if (formSpecs.schema.dependencies) {
    const uiSchemaByProperty = propertiesToUiSchema(
      // @ts-ignore
      formSpecs.schema.dependencies,
      uiSchema
    );
    for (const property in uiSchemaByProperty) {
      const uiSchema = uiSchemaByProperty[property];
      delete uiSchema['ui:classNames'];
      dependenciesUiSchema = {
        ...dependenciesUiSchema,
        ...uiSchema,
      };
    }
  }
  return {
    ...uiSchema,
    ...dependenciesUiSchema,
    ...propertiesToUiSchema(formSpecs.schema.properties, uiSchema),
  };
};

const cleanNestedEmptyObject = (object: GenericObjectType) => {
  Object.entries(object).forEach(([k, v]) => {
    if (v && typeof v === 'object') cleanNestedEmptyObject(v);
    if (v && typeof v === 'object' && !Object.keys(v).length) {
      delete object[k];
    }
  });
  return object;
};

const replaceRefs = (schema: FormSpecs, variables: GenericObjectType) => {
  let someEmptyReplacement = false;
  const result = JSON.stringify(schema).replace(
    /{\s*"\$ref"\s*:\s*"#\/variables\/(.+?)"\s*}/gm,
    (match, variable) => {
      if (!variables[variable]) {
        someEmptyReplacement = true;
        return '{}';
      }
      return JSON.stringify(variables[variable]);
    }
  );
  if (!someEmptyReplacement) {
    return result;
  }
  return JSON.stringify(cleanNestedEmptyObject(JSON.parse(result)));
};

const replaceVariables = (
  formSpecs: FormSpecs,
  options: EmbedFormOptions
): FormSpecs => {
  let i18nVariables = options?.i18nVariables?.[options.locale] ?? {};
  let i18nFallbackVariables =
    (options.fallbackLocale &&
      options?.i18nVariables?.[options.fallbackLocale]) ??
    {};
  if (options?.i18nVariables && options?.variables) {
    i18nVariables = JSON.parse(
      Mustache.render(JSON.stringify(i18nVariables), options.variables)
    );
    i18nFallbackVariables = JSON.parse(
      Mustache.render(JSON.stringify(i18nFallbackVariables), options.variables)
    );
  }
  const replacementVariables = {
    ...(options?.variables ?? {}),
    ...i18nFallbackVariables,
    ...i18nVariables,
  };
  return JSON.parse(
    Mustache.render(
      replaceRefs(formSpecs, replacementVariables),
      replacementVariables
    )
  ) as FormSpecs;
};

export function processFormSpecs(formSpecs: FormSpecs): ProcessedFormSpecs {
  const formSpecsWithReplacedVariables = replaceVariables(
    formSpecs,
    formSpecs.schema.embedFormOptions
  );
  return {
    schema: processSchema(formSpecsWithReplacedVariables.schema),
    uiSchema: processUiSchema(formSpecsWithReplacedVariables),
    embedFormOptions: {
      ...formSpecsWithReplacedVariables.schema.embedFormOptions,
      withBranding:
        formSpecsWithReplacedVariables.schema.embedFormOptions?.withBranding,
    },
  };
}
